<script setup lang="ts">
/**
 * Lista de un ranking del panel.
 *
 * Nace porque el bloque estaba copiado cuatro veces en `DashboardView.vue` y ya
 * había divergido entre las cuatro copias: una pintaba la posición, otra no; una
 * enseñaba el texto secundario en la barra y otra aparte; una llevaba la posición
 * a un ancho distinto. Cuatro rankings que se supone equivalentes no se pueden
 * mantener iguales a mano, y la diferencia no se ve hasta que se busca.
 *
 * Los cuatro datos del ranking —posición, nombre, barra y unidades— salen de
 * `toRanking()`, así que una fila siempre viene con su `percent` ya calculado
 * respecto al primero. Aquí no hay nada que decidir: solo pintar.
 *
 * @prop rows      Filas ya normalizadas por `toRanking()`.
 * @prop tone      Color de la barra. Cada ranking tiene el suyo para que se
 *                 distingan de un vistazo.
 * @prop secondary Si cada fila lleva su texto auxiliar —referencia, número de
 *                 cotizaciones— y de qué ancho es. `null` para los rankings que no
 *                 lo tienen, como los asesores.
 * @prop empty     Lo que se dice cuando no hay nada. Cada ranking tiene un vacío
 *                 distinto porque no es lo mismo no tener ventas que no tener
 *                 asesores: sin el motivo, un vacío parece siempre un fallo.
 * @prop testid    Para que los tests puedan apuntar a esta lista concreta y no a
 *                 la primera que encuentre.
 */

export interface RankRow {
  key: string
  /** Lo que identifica la fila: nombre del producto, del cliente, del asesor. */
  primary: string
  /** Lo que acompaña al nombre, si la lista lo lleva. */
  secondary: string | null
  /** El dato, ya formateado con su unidad: «5 uds.», «$ 1.234,00». */
  value: string
  /** Porcentaje respecto al primero, ya calculado. */
  percent: number
  tone: string
}

const props = withDefaults(
  defineProps<{
    rows: RankRow[]
    tone: string
    secondary?: number | null
    empty: string
    testid?: string
  }>(),
  {
    secondary: null,
    testid: undefined,
  },
)

/**
 * La posición solo se escribe si la lista es corta.
 *
 * En un ranking de diez, el número dice lo mismo que la barra y encima obliga a
 * mirar. En uno de treinta, el número va por delante de la barra y ahorra recorrer
 * la lista para encontrar el último. El corte se hace donde deja de ayudar.
 */
const MOSTRAR_POSICION_MAX = 12

function conPosicion(visible: boolean): boolean {
  return visible && props.rows.length <= MOSTRAR_POSICION_MAX
}
</script>

<template>
  <p
    v-if="rows.length === 0"
    class="py-8 text-center text-sm text-surface-500 dark:text-surface-400"
  >
    {{ empty }}
  </p>

  <ol
    v-else
    class="space-y-3"
    :data-testid="testid"
  >
    <li
      v-for="(row, index) in rows"
      :key="row.key"
    >
      <div class="flex items-baseline justify-between gap-3">
        <span class="flex min-w-0 items-baseline gap-2">
          <!--
            La posición va antes del nombre y no alineada con él: si comparte
            columna con el nombre, el truncado del nombre se come la posición en los
            nombres largos, que son los que más se leen.
          -->
          <span
            v-if="conPosicion(true)"
            class="w-4 shrink-0 text-sm font-semibold tabular-nums text-surface-400"
          >
            {{ index + 1 }}
          </span>
          <span class="min-w-0 truncate text-sm text-surface-900 dark:text-surface-100">
            {{ row.primary }}
          </span>
        </span>
        <span class="shrink-0 text-sm font-semibold tabular-nums text-surface-900 dark:text-surface-100">
          {{ row.value }}
        </span>
      </div>

      <div
        class="mt-1.5 flex items-center gap-2"
        :class="{ 'pl-6': conPosicion(true) }"
      >
        <span class="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-100 dark:bg-surface-800">
          <span
            :class="['block h-full rounded-full', row.tone]"
            :style="{ width: `${row.percent}%` }"
          />
        </span>

        <!--
          El texto auxiliar va en la misma línea que la barra y no debajo. Con la
          barra al 100 % y el texto al lado, se lee de una pasada; con el texto
          debajo, hay que mirar dos sitios por fila.

          El ancho es fijo para que las unidades de todas las filas alineen. Con
          ancho automático, cada fila tiene su texto a una distancia distinta y la
          columna deja de leerse como columna.
        -->
        <span
          v-if="secondary !== null && row.secondary !== null"
          class="shrink-0 truncate text-right font-mono text-xs text-surface-400"
          :style="{ width: `${secondary}px` }"
        >
          {{ row.secondary }}
        </span>
      </div>
    </li>
  </ol>
</template>